import Card from "@/design-system/Card/Card";
import { Check } from "@/design-system/Check/Check";
import { EvolutionUpgradePlayerCard } from "@/design-system/PlayerCard/PlayerCard";
import { StatChip } from "@/design-system/StatChip/StatChip";
import { NewTag } from "@/design-system/Tags/NewTag/NewTag";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { CoinIcon, arrow, pitchCardPlaceholder } from "@/designSystemIcons";
import { evoPathwayIcon, expire as expireIcon } from "@/icons";
import { cn } from "@/lib/utils";
import {
  Evolution,
  EvolutionCategory,
  isExpiringSoon,
} from "@/redux/evolutions/evolutionsSlice";
import { eventTokensSelector } from "@/redux/loadedContent/loadedContentSlice";
import {
  formatEvoCost,
  isFreeEvolution,
  isPathwayEvolution,
  matchesLabel,
  sortRequirementsForCard,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { formatTime } from "@repo/shared/utils";
import { CircleCheck, Dna, Lock } from "lucide-react";
import { memo, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  getSourceDisplay,
  useEvoRowLabel,
  useEvoRowValue,
} from "../EvolutionsBuilder/components/evoDisplayUtils";
import { evoStateItems, getEvoStateIcon } from "./EvolutionsOverviewPage";

const VISIBLE_REQUIREMENTS = 2;
const cdnBase = import.meta.env.VITE_APP_ASSETS_BASE_URL;

type Props = {
  evolution: Evolution;
  category?: EvolutionCategory;
  isSelected?: boolean;
  selectMode?: boolean;
  onClick?: () => void;
};

/** One evolution: its best upgrade, the key facts, deadlines and requirements. */
export const EvolutionOverviewCard = memo(function EvolutionOverviewCard({
  evolution,
  category,
  isSelected,
  selectMode,
  onClick,
}: Props) {
  const { t } = useTranslation(["evolutions", "common"]);
  const { t: tEvo } = useTranslation("evolutions");
  const topPlayer = evolution.topPlayers[0];
  const stateItem = evoStateItems.find((i) => i.state === evolution.state);

  const isFree = isFreeEvolution(evolution);
  const coins = evolution.cost?.coins ?? 0;
  const points = evolution.cost?.points ?? 0;
  const eventTokens = useSelector(eventTokensSelector);
  const { sourceIcon, sourceLabel, sourceValue } = useMemo(
    () =>
      getSourceDisplay(evolution.source, evolution.state, tEvo, eventTokens),
    [evolution.source, evolution.state, tEvo, eventTokens],
  );

  const now = Date.now();
  const unlockIn = evolution.unlockBy
    ? formatTime(evolution.unlockBy * 1000 - now)
    : null;
  const endsIn = evolution.completeBy
    ? formatTime(evolution.completeBy * 1000 - now)
    : null;

  return (
    <Card
      className={cn(
        "flex h-full cursor-pointer flex-col overflow-hidden",
        isSelected &&
          "border-primary-active bg-neutral-750 hover:border-primary-active",
      )}
    >
      <div className="flex h-full flex-col" onClick={onClick}>
        <div className="flex items-center gap-x-2 px-4 pt-3.5 pb-1">
          <span className="size-5 shrink-0 text-evolution-new [&>svg]:size-5">
            {isPathwayEvolution(evolution) ? evoPathwayIcon : <Dna />}
          </span>
          <span className="truncate text-sm mobile:text-base font-medium text-neutral-50">
            {evolution.name}
          </span>
          {evolution.newEvolution ? (
            <NewTag />
          ) : (
            isExpiringSoon(evolution) && <NewTag tag={t("expiresSoon")} />
          )}
          {selectMode ? (
            <CircleCheck
              className={cn(
                "ml-auto size-5 shrink-0",
                isSelected ? "text-primary-active" : "text-foreground-disabled",
              )}
            />
          ) : (
            category && (
              <span className="ml-auto shrink-0 text-xs text-foreground-muted">
                {category.name}
              </span>
            )
          )}
        </div>

        <div className="px-3 pt-3">
          {topPlayer ? (
            <EvolutionUpgradePlayerCard
              basePlayer={topPlayer.basePlayer}
              evolutionPlayer={topPlayer.evolutionPlayer}
              gap="gap-1"
              hideBackground
              hidePrice
              showBasePlayerMetaRating
            />
          ) : (
            <div className="flex h-60 items-center justify-center gap-1">
              <div className="w-25 text-foreground-disabled">
                {pitchCardPlaceholder}
              </div>
              <div className="size-4 shrink-0 -rotate-90">{arrow}</div>
              <div className="w-25 text-foreground-disabled">
                {pitchCardPlaceholder}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col px-3 pt-4 pb-3">
          {/* State, and where it unlocks from. */}
          <div className="mb-1 flex items-center gap-3 border-b border-neutral-750 px-3 pt-1 pb-2 text-sm text-foreground">
            <span className="flex shrink-0 items-center gap-2 text-foreground-muted">
              <span className="flex size-4 items-center justify-center">
                {getEvoStateIcon(evolution.state, 16)}
              </span>
              {stateItem && <span>{t(stateItem.labelKey)}</span>}
            </span>
            {(sourceIcon || sourceValue) && (
              <span className="ml-auto flex min-w-0 items-center gap-2">
                {sourceIcon && (
                  <span className="flex size-4 shrink-0 items-center justify-center">
                    {sourceIcon}
                  </span>
                )}
                <span className="truncate font-semibold">
                  {evolution.source?.type === "token-store"
                    ? (evolution.source.cost ?? sourceLabel)
                    : (sourceValue ?? sourceLabel)}
                </span>
              </span>
            )}
          </div>
          <div className="px-3">
            <StatChip
              variant="row"
              className="py-1"
              label={t("footer.games")}
              value={matchesLabel(evolution.matches)}
              icon={
                <img
                  src={`${cdnBase}/misc/ea/ball.png`}
                  alt={t("alt.matches")}
                />
              }
            />
            {evolution.source?.type !== "token-store" && (
              <StatChip
                variant="row"
                className="py-1"
                label={t("header.price")}
                icon={<CoinIcon size={16} />}
                value={
                  isFree ? (
                    t("common:free")
                  ) : (
                    <span className="flex items-center gap-x-2">
                      {coins > 0 && formatEvoCost(coins)}
                      {coins > 0 && points > 0 && (
                        <span className="size-1 rounded-full bg-foreground-disabled" />
                      )}
                      {points > 0 && (
                        <span className="flex items-center gap-x-1">
                          <img
                            alt={t("common:alt.points")}
                            className="size-4"
                            src={`${cdnBase}/fc24/misc/points.png`}
                          />
                          {points}
                        </span>
                      )}
                    </span>
                  )
                }
                valueClassName={isFree ? "text-evolution-secondary" : undefined}
              />
            )}
            {unlockIn && (
              <StatChip
                variant="row"
                className="py-1"
                label={t("header.unlockBy")}
                value={unlockIn}
                icon={<Lock className="size-4" />}
              />
            )}
            {endsIn && (
              <StatChip
                variant="row"
                className="py-1"
                label={t("header.completeBy")}
                value={endsIn}
                icon={<span className="size-4 fill-current">{expireIcon}</span>}
              />
            )}
          </div>
        </div>

        <Requirements evolution={evolution} />
      </div>
    </Card>
  );
});

const Requirements = ({ evolution }: { evolution: Evolution }) => {
  const { t } = useTranslation(["evolutions", "common"]);
  const rowLabel = useEvoRowLabel();
  const rowValue = useEvoRowValue();
  const [expanded, setExpanded] = useState(false);
  const sorted = useMemo(
    () => sortRequirementsForCard(evolution.requirements ?? []),
    [evolution.requirements],
  );
  const visible = expanded ? sorted : sorted.slice(0, VISIBLE_REQUIREMENTS);
  const hidden = sorted.length - VISIBLE_REQUIREMENTS;

  return (
    <div className="mt-auto flex flex-col border-t border-neutral-750 px-4 pt-3 pb-2">
      <span className="text-sm font-semibold text-foreground">
        {t("sections.requirements")}
      </span>
      {sorted.length === 0 && (
        <span className="flex min-h-7 items-center text-sm text-foreground-muted">
          {t("card.noRequirementsShown")}
        </span>
      )}
      {visible.map((r) => (
        <div
          key={`${r.key}-${r.value}`}
          className="flex min-h-7 items-center gap-x-2 text-sm"
        >
          <Check className="mt-0 text-upgrade" />
          <span className="truncate text-foreground-muted">{rowLabel(r)}</span>
          <span className="ml-auto truncate text-right text-foreground">
            {rowValue(r)}
          </span>
        </div>
      ))}
      {hidden > 0 && (
        <TextButton
          size="medium"
          className="mx-auto mt-1"
          // Inside the card's link: expanding must not open the evolution.
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {expanded
            ? t("common:showLess")
            : t("card.moreRequirements", { count: hidden })}
        </TextButton>
      )}
    </div>
  );
};

export const EvolutionOverviewCardSkeleton = () => (
  <Card hoverable={false} className="h-120 w-full animate-pulse">
    {null}
  </Card>
);
